import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import {
  FilterSelect,
  HpButton,
  HpCardHeader,
  PageHeading,
  SearchField,
  Segmented,
  Toolbar,
  ToolbarSpacer,
} from '@hp/ui';
import { useCan } from '../api/me';
import { trpc } from '../api/trpc';
import { ErrorAlert } from '../components/error-alert';
import { CampaignMapTable } from '../features/performance/mapping-campaigns';
import { ProjectMapTable } from '../features/performance/mapping-projects';
import { invalidateMapping, useCampaignMap, useProjectMap, type MapFilter } from '../features/performance/queries';
import { fmtInt } from '../lib/format';

const KINDS = ['campaigns', 'projects'] as const;
type Kind = (typeof KINDS)[number];
const FILTERS = ['unmapped', 'suggested', 'confirmed', 'rejected'] as const satisfies readonly MapFilter[];
const PROVIDERS = ['meta', 'google', 'sklik'] as const;
type Provider = (typeof PROVIDERS)[number];

/** The identity maps behind the campaign view: ad campaigns ↔ CRM campaigns, CRM projects ↔ Back Office projects. */
export default function PerformanceMappings() {
  const { t } = useTranslation(['performance', 'common']);
  const navigate = useNavigate();
  const canManage = useCan()('performance.manage');
  const [kind, setKind] = useState<Kind>('campaigns');
  const [filter, setFilter] = useState<MapFilter | ''>('');
  const [provider, setProvider] = useState<Provider | ''>('');
  const [q, setQ] = useState('');

  const campaigns = useCampaignMap({
    filter: filter || undefined,
    provider: provider || undefined,
    q: q || undefined,
    limit: 300,
  });
  const projects = useProjectMap({ filter: filter || undefined, q: q || undefined, limit: 300 });
  const suggestCampaigns = useMutation(
    trpc.performance.mapping.suggestCampaigns.mutationOptions({ onSuccess: invalidateMapping }),
  );
  const suggestProjects = useMutation(
    trpc.performance.mapping.suggestProjects.mutationOptions({ onSuccess: invalidateMapping }),
  );
  const current = kind === 'campaigns' ? campaigns : projects;
  const suggest = kind === 'campaigns' ? suggestCampaigns : suggestProjects;
  const counts = current.data?.counts;

  return (
    <div className="bo-sections">
      <PageHeading
        title={t('mapping.title')}
        description={t('mapping.description')}
        actions={
          <HpButton variant="secondary" icon="arrowLeft" onClick={() => void navigate('/marketing/performance')}>
            {t('mapping.backToPerformance')}
          </HpButton>
        }
      />

      <section className="hp-card">
        <HpCardHeader title={t(`mapping.${kind}.title`)} subtitle={t(`mapping.${kind}.subtitle`)} icon="link">
          {canManage && (
            <HpButton
              variant="secondary"
              icon="spark"
              className="bo-button--sm"
              busy={suggest.isPending}
              onClick={() => suggest.mutate()}
            >
              {t('mapping.actions.suggest')}
            </HpButton>
          )}
        </HpCardHeader>
        <Toolbar className="bo-toolbar">
          <Segmented
            ariaLabel={t('mapping.kindAria')}
            value={kind}
            options={KINDS.map((k) => ({ value: k, label: t(`mapping.${k}.tab`) }))}
            onChange={setKind}
          />
          <FilterSelect
            value={filter}
            aria-label={t('mapping.filters.statusAria')}
            onChange={(e) => setFilter(e.target.value as MapFilter | '')}
          >
            <option value="">{t('mapping.filters.all')}</option>
            {FILTERS.map((f) => (
              <option key={f} value={f}>
                {t(`mapping.status.${f}`)} {counts ? `(${fmtInt(counts[f])})` : ''}
              </option>
            ))}
          </FilterSelect>
          {kind === 'campaigns' && (
            <FilterSelect
              value={provider}
              aria-label={t('mapping.filters.providerAria')}
              onChange={(e) => setProvider(e.target.value as Provider | '')}
            >
              <option value="">{t('mapping.filters.allProviders')}</option>
              {PROVIDERS.map((p) => (
                <option key={p} value={p}>
                  {t(`channel.${p}`)}
                </option>
              ))}
            </FilterSelect>
          )}
          <ToolbarSpacer />
          <SearchField
            value={q}
            placeholder={t('mapping.filters.search')}
            onChange={(e) => setQ(e.target.value)}
            aria-label={t('mapping.filters.search')}
          />
        </Toolbar>
        {suggest.data && (
          <p className="hp-card__body bo-notice" role="status">
            {t('mapping.suggested', {
              examined: suggest.data.examined,
              exact: suggest.data.exact,
              similar: suggest.data.similar,
            })}
          </p>
        )}
        <ErrorAlert error={suggest.error ?? current.error} className="bo-alert--inset" />
        {current.isLoading ? (
          <p className="hp-card__body bo-muted">{t('common:actions.loading')}</p>
        ) : kind === 'campaigns' ? (
          <CampaignMapTable rows={campaigns.data?.rows ?? []} canManage={canManage} />
        ) : (
          <ProjectMapTable rows={projects.data?.rows ?? []} canManage={canManage} />
        )}
        <p className="hp-card__body bo-hint">{t(`mapping.${kind}.hint`)}</p>
      </section>
    </div>
  );
}
